import { CFDFile } from '@aero-platform/shared'

type CaseLike = { simTemplate?: CFDFile }

/**
 * Run-level "Replace SIM File": a single override wins for the whole run, so stamp it onto
 * EVERY case (overwrite-all). Returns new case objects and never mutates the inputs.
 */
export const applyOverrideToAllCases = <T extends CaseLike>(
  cases: T[],
  overrideTemplate: CFDFile | Record<string, unknown>
): T[] => (cases || []).map(c => ({ ...c, simTemplate: overrideTemplate as CFDFile }))

/**
 * New-run top-level default: fill a case's simTemplate from the run-level default ONLY when the
 * case has none of its own, so a per-case upload always wins. Mutates caseItem in place.
 */
export const fillSimTemplateIfMissing = (caseItem: CaseLike, topLevelSim?: CFDFile): void => {
  if (topLevelSim && !caseItem.simTemplate) {
    caseItem.simTemplate = { ...topLevelSim } as CFDFile
  }
}

/**
 * True when a case's simTemplate is a deliberate per-case upload — a .sim uploaded onto a single
 * case, which lands in the permanent /sim-templates/ folder (rootType 'override'). These must be
 * preserved when a run-level default is applied. A run-level override instead points at
 * /staging_overrides/, so it is NOT treated as a per-case upload.
 */
export const isPerCaseUpload = (sim?: CFDFile): boolean =>
  (sim?.rootType || '').toLowerCase() === 'override' &&
  (sim?.path || '').includes('/sim-templates/')

/**
 * New-run run-level default: apply the run-level default template (an uploaded/picked run-level
 * override, or the methodology default) to every case EXCEPT deliberate per-case uploads. Returns
 * new case objects only when something changed; never mutates inputs.
 */
export const applyRunLevelDefaultToCases = <T extends CaseLike>(
  cases: T[],
  runLevelDefault: CFDFile
): { cases: T[]; changed: boolean } => {
  let changed = false
  const next = (cases || []).map(c => {
    if (isPerCaseUpload(c.simTemplate)) return c
    const sameFile = c.simTemplate?.file === runLevelDefault.file
    const samePath = (c.simTemplate?.path || '') === (runLevelDefault.path || '')
    if (sameFile && samePath) return c
    changed = true
    return { ...c, simTemplate: runLevelDefault }
  })
  return { changed, cases: changed ? next : cases }
}

/**
 * Resubmit modal: the sim is "changed" only when a non-empty selection differs from the case's
 * current sim. When unchanged, the resubmit passes no template so the case is left untouched.
 */
export const isSimTemplateChanged = (currentSimFile: string, selectedSimFile: string): boolean =>
  !!selectedSimFile && selectedSimFile !== currentSimFile

/**
 * Resubmit modal (identity-aware): a selection is "changed" when the user has chosen/uploaded a
 * template whose FULL identity (file AND blob path) differs from the case's current sim. Filename
 * alone is not enough — two different sims can share a filename but live at different blob paths
 * (a per-case upload lands in /sim-templates/{id}/, a dropdown pick points at another source). If
 * we compared filenames only, such a pick would be treated as "no change" and the resubmit would
 * silently leave the case on its old sim (the "didn't overwrite" bug).
 */
export const hasSimTemplateChanged = (
  current?: CFDFile | null,
  selected?: CFDFile | null
): boolean => {
  if (!selected || !selected.file) return false
  const currentFile = current?.file || ''
  const currentPath = current?.path || ''
  const selectedFile = selected.file || ''
  const selectedPath = selected.path || ''
  return selectedFile !== currentFile || selectedPath !== currentPath
}
